/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import './PrivacyNotice.scss';
import alfanLogo from '../../assets/images/alfan_logo.svg';
import PageMeta from '../../components/PageMeta/PageMeta';

const PrivacyNotice = () => {
  return (
    <div className="pn-wrapper">
      <PageMeta
        title="Privacy Policy"
        description="Read how Alfan collects, uses, and protects your personal information, including identity verification through Persona."
        path="/privacy-policy"
      />
      <header className="pn-header">
        <div className="pn-header__inner">
          <img src={alfanLogo} alt="Alfan" className="pn-header__logo" />
          <span className="pn-header__pill">Privacy Policy</span>
        </div>
      </header>

      <main className="pn-main">
        <div className="pn-container">

          <div className="pn-hero">
            <h1>Privacy Policy</h1>
            <p>Alfan Digital Media Group FZ-LLC &mdash; Last Updated: May 2026</p>
          </div>


          <div className="pn-intro">
            <p>
              The Alfan Group and its affiliates (collectively, "Alfan," "we," or "us") is the
              owner of the Alfan platform. Alfan wants you to be familiar with how we collect,
              use and disclose personal information when you visit our website{' '}
              <a href="https://alfangroup.com">https://alfangroup.com</a>, use our Platform or
              enter into a contractual agreement with us for one of our Services.
            </p>
            <p>
              We know that providing personal information is an act of trust and we take that
              seriously. If you have any questions or requests about your personal information,
              please email{' '}
              <a href="mailto:privacy@alfangroup.com">privacy@alfangroup.com</a>.
            </p>
            <p>
              This Privacy Notice supplements other notices and is not intended to override them.
              This website may include links to third-party websites, plug-ins and applications.
              When you leave our website, we encourage you to read the Privacy Notice of every
              website you visit.
            </p>
            <div className="pn-contact">
              <span>Website: <a href="https://alfangroup.com">alfangroup.com</a></span>
              <span>Email: <a href="mailto:privacy@alfangroup.com">privacy@alfangroup.com</a></span>
            </div>
          </div>

          {/* ── Section 1 ── */}
          <section className="pn-section" id="section-1">
            <div className="pn-section__header">
              <span className="pn-section__num">1</span>
              <h2>How We Use Personal Information</h2>
            </div>
            <div className="pn-section__body">

              <h3>Information that you provide to us</h3>
              <div className="pn-table-wrapper">
                <table className="pn-table">
                  <thead>
                    <tr>
                      <th>Categories of personal information</th>
                      <th>Purposes of use</th>
                      <th>Source</th>
                      <th>Legal basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Categories">
                        Creators: Contact details (name, email, contact number)
                      </td>
                      <td data-label="Purposes">
                        Provide our Services and register your account on the platform
                      </td>
                      <td data-label="Source">You, as a Creator signing up on the Platform</td>
                      <td data-label="Legal basis">Performance of contract</td>
                    </tr>
                    <tr>
                      <td data-label="Categories">AML documentation</td>
                      <td data-label="Purposes">At onboarding stage for compliance reasons</td>
                      <td data-label="Source">Legal obligation</td>
                      <td data-label="Legal basis">Legal obligation</td>
                    </tr>
                    <tr>
                      <td data-label="Categories">Analytics and preferences</td>
                      <td data-label="Purposes">
                        Marketing relevant products and personalising your experience
                      </td>
                      <td data-label="Source">Legitimate interests</td>
                      <td data-label="Legal basis">Legitimate interests</td>
                    </tr>
                    {/* CHANGE 1 — New Persona/KYC row */}
                    <tr className="pn-table__row--highlight">
                      <td data-label="Categories">
                        <strong>Identity verification data:</strong> government-issued ID
                        document (front and back), selfie/facial image, form-submitted details
                        (name, country, account type), data derived from ID scan (e.g. date of
                        birth, nationality, document number, issue date), and location metadata
                        associated with the KYC scan.
                      </td>
                      <td data-label="Purposes">
                        To verify your identity, prevent fraud, and meet our legal and regulatory
                        compliance obligations prior to enabling certain platform features.
                      </td>
                      <td data-label="Source">
                        You, directly, via the identity verification flow powered by our
                        third-party provider, Persona.
                      </td>
                      <td data-label="Legal basis">Consent; Legal obligation.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3>Users following our Creators</h3>
              <div className="pn-table-wrapper">
                <table className="pn-table">
                  <thead>
                    <tr>
                      <th>Categories of personal information</th>
                      <th>Purposes of use</th>
                      <th>Source</th>
                      <th>Legal basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Categories">
                        Users following Creators (name, email, phone number)
                      </td>
                      <td data-label="Purposes">
                        To provide paid-for services via our third-party provider Tap Payments
                      </td>
                      <td data-label="Source">
                        Creators, Buyers, Third party vendors, Publicly available sources
                      </td>
                      <td data-label="Legal basis">Legitimate interests</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3>Information for business-to-business relationships</h3>
              <div className="pn-table-wrapper">
                <table className="pn-table">
                  <thead>
                    <tr>
                      <th>Categories of personal information</th>
                      <th>Purposes of use</th>
                      <th>Source</th>
                      <th>Legal basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Categories">Business contact information</td>
                      <td data-label="Purposes">Build and manage B2B relationships</td>
                      <td data-label="Source">
                        You, Creators, Buyers, Third party vendors, Media partners
                      </td>
                      <td data-label="Legal basis">
                        Performance of contract, Legitimate interests, Legal obligation
                      </td>
                    </tr>
                    <tr>
                      <td data-label="Categories">Content</td>
                      <td data-label="Purposes">Build and manage B2B relationships</td>
                      <td data-label="Source">
                        You, Creators, Buyers, Third party vendors, Publicly available sources
                      </td>
                      <td data-label="Legal basis">
                        Performance of contract, Legitimate interests
                      </td>
                    </tr>
                    <tr>
                      <td data-label="Categories">
                        Transactional information (as permitted by law)
                      </td>
                      <td data-label="Purposes">Build and manage B2B relationships</td>
                      <td data-label="Source">
                        You, Creators, Buyers, Third party vendors, Publicly available sources
                      </td>
                      <td data-label="Legal basis">
                        Performance of contract, Legitimate interests
                      </td>
                    </tr>
                    <tr>
                      <td data-label="Categories">
                        Due diligence and AML information (as permitted by law)
                      </td>
                      <td data-label="Purposes">
                        Compliance and risk management for B2B relationships
                      </td>
                      <td data-label="Source">
                        Your responses to due diligence questions, third-party risk screening tools
                      </td>
                      <td data-label="Legal basis">
                        Performance of contract, Legitimate interests, Legal obligation
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3>Information collected automatically from you and/or your device</h3>
              <div className="pn-table-wrapper">
                <table className="pn-table">
                  <thead>
                    <tr>
                      <th>Categories of personal information</th>
                      <th>Purposes of use</th>
                      <th>Source</th>
                      <th>Legal basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Categories">Device information and identifiers</td>
                      <td data-label="Purposes">
                        Provide our Services, personalise your experience, send relevant
                        marketing and advertising
                      </td>
                      <td data-label="Source">
                        You, Creators, Buyers, Third party service providers
                      </td>
                      <td data-label="Legal basis">Legitimate interests, Legal obligation</td>
                    </tr>
                    <tr>
                      <td data-label="Categories">
                        Connection and Usage (including cookie data)
                      </td>
                      <td data-label="Purposes">
                        Provide our Services, personalise your experience, marketing, safety,
                        B2B relationships
                      </td>
                      <td data-label="Source">You, Publicly available sources</td>
                      <td data-label="Legal basis">
                        Legitimate interests, Performance of contract
                      </td>
                    </tr>
                    <tr>
                      <td data-label="Categories">Geolocation</td>
                      <td data-label="Purposes">
                        Provide our Services, personalise your experience, marketing, safety,
                        B2B relationships
                      </td>
                      <td data-label="Source">
                        Your responses to due diligence questions, third-party risk screening tools
                      </td>
                      <td data-label="Legal basis">Legitimate interests</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3>Information collected from third parties</h3>
              <div className="pn-table-wrapper">
                <table className="pn-table">
                  <thead>
                    <tr>
                      <th>Categories of personal information</th>
                      <th>Purposes of use</th>
                      <th>Source</th>
                      <th>Legal basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td data-label="Categories">
                        Information from public and commercial sources
                      </td>
                      <td data-label="Purposes">Build and manage B2B relationships</td>
                      <td data-label="Source">
                        Advertising providers, analytics providers, marketers, partners, third
                        party vendors, public records databases, content partners
                      </td>
                      <td data-label="Legal basis">Legitimate interests</td>
                    </tr>
                    <tr>
                      <td data-label="Categories">
                        Third party partners in connection with providing our Services
                      </td>
                      <td data-label="Purposes">
                        Provide our Services, personalise your experience, analytics, safety,
                        marketing and advertising
                      </td>
                      <td data-label="Source">
                        Advertising providers, analytics providers, marketers, partners, third
                        party vendors, public records databases
                      </td>
                      <td data-label="Legal basis">
                        Performance of contract, Legitimate interests, Legal obligation
                      </td>
                    </tr>
                    <tr>
                      <td data-label="Categories">
                        Due diligence information (where permitted by law)
                      </td>
                      <td data-label="Purposes">
                        Due diligence checks on customers, partners and suppliers; compliance and
                        risk management
                      </td>
                      <td data-label="Source">
                        Your responses to due diligence questions, third-party risk screening tools
                      </td>
                      <td data-label="Legal basis">
                        Legal obligation, Performance of contract, Legitimate interests
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* ── CHANGE 2 — New Section 2: Identity Verification & Biometric Data Processing ── */}
          <section className="pn-section pn-section--kyc" id="section-2">
            <div className="pn-section__header">
              <span className="pn-section__num">2</span>
              <h2>Identity Verification &amp; Biometric Data Processing</h2>
            </div>
            <div className="pn-section__body">

              <div className="pn-subsection">
                <p className="pn-subsection__label">2.1 — How We Verify Your Identity</p>
                <p>
                  To access certain features of the Alfan platform, we are required to verify your
                  identity. We use a third-party identity verification provider,{' '}
                  <strong>Persona</strong>, to facilitate this process. The categories of personal
                  data collected during verification are detailed in the table in Section 1 above.
                  Identity verification is required to unlock these features; if you do not complete
                  verification, the relevant features will remain unavailable to you. If you withdraw
                  consent or your verification is unsuccessful, any data collected during the
                  verification attempt will be deleted within 30 days.
                </p>
              </div>

              <div className="pn-subsection">
                <p className="pn-subsection__label">
                  2.2 — Roles: Alfan as Data Controller, Persona as Data Processor
                </p>
                <p>
                  Alfan acts as the <strong>data controller</strong> for all identity verification
                  data collected through this process. Persona acts as a{' '}
                  <strong>data processor</strong>, processing your biometric information on Alfan's
                  behalf strictly for the purposes of identity verification and fraud prevention.
                </p>
              </div>

              <div className="pn-subsection">
                <p className="pn-subsection__label">2.3 — Biometric Data</p>
                <p>
                  Persona processes your facial image to perform a liveness check and match it
                  against your ID document photograph. Persona does not retain biometric identifiers
                  (such as facial recognition vectors or templates) after the verification process
                  is complete. The raw images of your ID document and selfie are retained by Alfan
                  for the period described in Section 4. Neither Alfan nor Persona uses your
                  identity verification data or biometric information to train any artificial
                  intelligence or machine learning models. For information on how Persona processes
                  your data, please review Persona's Privacy Policy at{' '}
                  <a
                    href="https://withpersona.com/legal/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    withpersona.com/legal/privacy-policy
                  </a>
                  .
                </p>
              </div>

              <div className="pn-subsection">
                <p className="pn-subsection__label">2.4 — No Use for Model Training</p>
                <p>
                  Your identity verification data and biometric information are not used to train
                  any artificial intelligence or machine learning models by Alfan or by Persona.
                </p>
              </div>

            </div>
          </section>

          {/* ── Section 3 ── */}
          <section className="pn-section" id="section-3">
            <div className="pn-section__header">
              <span className="pn-section__num">3</span>
              <h2>How We Share Your Personal Information</h2>
            </div>
            <div className="pn-section__body">
              <ul>
                <li>
                  To third parties where necessary to manage and operate our business, including
                  management of contracts and providing the functionality of our Services.
                </li>
                <li>
                  To service providers including website hosting, IT infrastructure, analytics, and
                  email delivery service providers.
                </li>
                {/* CHANGE 3 — Persona bullet */}
                <li className="pn-list--highlight">
                  <strong>
                    To Persona (
                    <a href="https://withpersona.com" target="_blank" rel="noreferrer">
                      withpersona.com
                    </a>
                    )
                  </strong>
                  , our identity verification partner, who acts as a data processor on Alfan's
                  behalf. Persona processes your government-issued ID document and facial image
                  solely for the purposes of identity verification and fraud prevention. Persona
                  does not retain biometric identifiers after verification is complete, and does not
                  use your data for model training. For more information, see Persona's Privacy
                  Policy at{' '}
                  <a
                    href="https://withpersona.com/legal/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    withpersona.com/legal/privacy-policy
                  </a>
                  .
                </li>
                <li>
                  For legal and compliance reasons, including responding to requests and legal
                  demands from regulators or other authorities.
                </li>
                <li>For fraud prevention and security.</li>
                <li>To comply with applicable law and regulations.</li>
                <li>
                  In the event of any reorganisation, merger, sale, joint venture, assignment,
                  transfer, or other disposition of all or any portion of our business, assets, or
                  stock. You will be notified of any such transaction in accordance with applicable
                  law.
                </li>
              </ul>
            </div>
          </section>

          {/* ── Section 4 ── */}
          <section className="pn-section" id="section-4">
            <div className="pn-section__header">
              <span className="pn-section__num">4</span>
              <h2>How Long We Keep Your Data</h2>
            </div>
            <div className="pn-section__body">
              <p>
                Generally, we will retain Creator data for as long as they are active users on the
                platform. Where users have deactivated their accounts, we will retain data for 12
                months before deleting, unless required to retain it longer for regulatory or
                compliance reasons.
              </p>
              <p>
                We will retain your personal information for the period necessary to fulfil the
                purposes outlined in this Privacy Notice unless a longer retention period is
                required or permitted by law.
              </p>
              <p>
                The criteria used to determine our retention periods include: (i) the length of
                time we have an ongoing relationship with you; (ii) whether there is a legal
                obligation to which we are subject; or (iii) whether retention is advisable in
                light of our legal position.
              </p>

              {/* CHANGE 4 — Identity verification retention callout */}
              <div className="pn-callout">
                <p>
                  We retain <strong>identity verification data</strong> — including
                  government-issued ID images, selfie images, form-submitted details, data derived
                  from the ID scan, and location metadata collected during verification — for the
                  duration of your account and for{' '}
                  <strong>12 months following account deactivation</strong>, unless a longer period
                  is required by applicable law or regulation. Biometric information processed by
                  Persona during the verification flow is not retained by Persona beyond the
                  completion of the verification process.
                </p>
              </div>

              <p><strong>Retention Periods Based on Legal Obligations</strong></p>
              <ul>
                <li>
                  <strong>Cooperation with authorities:</strong> If we receive a preservation order
                  or search warrant related to your account, we will preserve the relevant personal
                  information after you delete your account.
                </li>
                <li>
                  <strong>Tax and accounting:</strong> We may retain financial and relationship
                  history as required by local tax law and bookkeeping requirements.
                </li>
                <li>
                  <strong>Legal action:</strong> We may retain relevant personal information in the
                  event of a legal claim or complaint for the duration of applicable limitation
                  periods.
                </li>
              </ul>
              <p>
                <strong>Anonymisation of Data</strong> — In some circumstances we will anonymise
                your personal information for research or statistical purposes, in which case we
                may use this information indefinitely without further notice to you.
              </p>
            </div>
          </section>

          {/* ── Section 5 ── */}
          <section className="pn-section" id="section-5">
            <div className="pn-section__header">
              <span className="pn-section__num">5</span>
              <h2>How We Keep Your Data Secure</h2>
            </div>
            <div className="pn-section__body">
              <p>
                We seek to use reasonable organisational, technical and administrative measures to
                protect personal information within our organisation, such as encryption and multi
                factor authentication. Unfortunately, no data transmission or storage system can be
                guaranteed to be 100% secure. If you have reason to believe that your interaction
                with us is no longer secure, please immediately notify us.
              </p>
            </div>
          </section>

          {/* ── Section 6 ── */}
          <section className="pn-section" id="section-6">
            <div className="pn-section__header">
              <span className="pn-section__num">6</span>
              <h2>Third-Party Services</h2>
            </div>
            <div className="pn-section__body">
              <p>
                This Privacy Notice does not address, and we are not responsible for, the privacy,
                information, or other practices of any third parties, including any third-party
                operating any website or service to which our products or services link. The
                inclusion of a link via any of our services does not imply endorsement of the linked
                site or service by us or by our affiliates.
              </p>
            </div>
          </section>

          {/* ── Section 7 ── */}
          <section className="pn-section" id="section-7">
            <div className="pn-section__header">
              <span className="pn-section__num">7</span>
              <h2>Children</h2>
            </div>
            <div className="pn-section__body">
              <div className="pn-subsection">
                <p className="pn-subsection__label">User Eligibility</p>
                <p>
                  Our Services are intended for users over the age of 18. We do not knowingly
                  collect personal information from children. We encourage responsible usage and
                  adherence to all applicable laws regarding online interactions and content sharing.
                </p>
              </div>
              <div className="pn-subsection">
                <p className="pn-subsection__label">
                  Prohibition of Child Sexual Abuse and Exploitation (CSAE)
                </p>
                <p>
                  We have a zero-tolerance policy against child sexual abuse and exploitation. Our
                  app strictly prohibits users from creating, uploading, or distributing any content
                  that facilitates the exploitation or abuse of children. Any violation will result
                  in immediate removal and reporting to the appropriate authorities.
                </p>
              </div>
              <div className="pn-subsection">
                <p className="pn-subsection__label">In-App Reporting Mechanism</p>
                <p>
                  We provide an in-app reporting feature that allows users to report any
                  inappropriate content or behavior. Reports are reviewed promptly, and appropriate
                  action is taken, including content removal and account suspension or termination.
                </p>
              </div>
              <div className="pn-subsection">
                <p className="pn-subsection__label">Content Moderation Practices</p>
                <p>
                  Our team actively reviews reported content to ensure compliance with community
                  guidelines and child safety standards.
                </p>
              </div>
              <div className="pn-subsection">
                <p className="pn-subsection__label">Parental Guidance</p>
                <p>
                  We encourage parents and guardians to be aware of their children's online
                  activities and take appropriate precautions, including device-level parental
                  controls where necessary.
                </p>
              </div>
              <div className="pn-subsection">
                <p className="pn-subsection__label">Child Safety Point of Contact</p>
                <p>
                  For any concerns regarding child safety, please contact our designated Child
                  Safety Officer at{' '}
                  <a href="mailto:info@alfangroup.com">info@alfangroup.com</a>.
                </p>
              </div>
            </div>
          </section>

          {/* ── Section 8 ── */}
          <section className="pn-section" id="section-8">
            <div className="pn-section__header">
              <span className="pn-section__num">8</span>
              <h2>International Transfers</h2>
            </div>
            <div className="pn-section__body">
              <p>
                Alfan has its headquarters in the United Arab Emirates. We may transfer your
                information to service providers and other third parties located outside of your
                country of residence. Data privacy laws vary from country to country and may not
                be equivalent to the laws in your home country. We take steps to ensure that
                reasonable safeguards are in place, including data transfer agreements. By providing
                us with your information, you acknowledge any such transfer, storage or use.
              </p>
            </div>
          </section>

          {/* ── Section 9 ── */}
          <section className="pn-section" id="section-9">
            <div className="pn-section__header">
              <span className="pn-section__num">9</span>
              <h2>Your Rights</h2>
            </div>
            <div className="pn-section__body">
              <p>
                You may be able to exercise certain privacy rights depending on our reason for
                processing your personal information and your location (e.g. UAE, EU, UK, or
                California). You may have the following rights:
              </p>
              <ul>
                <li>
                  <strong>Right to access:</strong> Request confirmation and copies of your personal
                  data we process.
                </li>
                <li>
                  <strong>Right to rectification:</strong> Request that we correct inaccurate or
                  incomplete personal information.
                </li>
                <li>
                  <strong>Right to portability:</strong> Request transfer of your personal
                  information to another organisation, in a structured, machine-readable format.
                </li>
                <li>
                  <strong>Right to erasure:</strong> Request deletion of your personal information,
                  subject to conditions and limitations under the law.
                </li>
                <li>
                  <strong>Right to revoke consent:</strong> Withdraw consent at any time with
                  future effect, without affecting the lawfulness of prior processing.
                </li>
                <li>
                  <strong>Right to object:</strong> Object to our processing of your personal
                  information under certain conditions, including profiling for direct marketing.
                </li>
                <li>
                  <strong>Right to opt-out of sale or sharing:</strong> We do not sell your
                  personal information for monetary consideration. You may have the right to opt out
                  of disclosures for third-party marketing purposes.
                </li>
                <li>
                  <strong>Right to limit sensitive data use:</strong> We only use sensitive personal
                  information for the purposes for which it was collected or with your consent.
                </li>
                <li>
                  <strong>Right to non-discrimination:</strong> We will not discriminate against
                  you for exercising any privacy rights.
                </li>
              </ul>
              <p>
                To exercise any of your rights, please submit a request to{' '}
                <a href="mailto:privacy@alfangroup.com">privacy@alfangroup.com</a>. You will need
                to verify your identity before we can fulfil your request. We will respond within
                any prescribed timelines under applicable law.
              </p>
            </div>
          </section>

          {/* ── Section 10 ── */}
          <section className="pn-section" id="section-10">
            <div className="pn-section__header">
              <span className="pn-section__num">10</span>
              <h2>Updates to This Notice</h2>
            </div>
            <div className="pn-section__body">
              <p>
                The "LAST UPDATED" date at the top of this Privacy Notice indicates when it was
                last revised. Any changes will become effective when we post the revised Privacy
                Notice.
              </p>
            </div>
          </section>

        </div>
      </main>

      <footer className="pn-footer">
        <div className="pn-footer__inner">
          <img src={alfanLogo} alt="Alfan" className="pn-footer__logo" />
          <p>© {new Date().getFullYear()} Alfan Digital Media Group FZ-LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyNotice;
